import type { Over, UniqueIdentifier } from '@dnd-kit/core';
import { hasSortableData } from '@dnd-kit/sortable';
import type { KanbanDragData } from './types';

export type KanbanDropTarget =
  | { kind: 'zone'; zoneId: string }
  | { kind: 'column'; toColumnId: number; toIndex: number };

/**
 * Work out what a drag was dropped on.
 *
 * Pure and React-free so it can be tested without a DOM — which matters here,
 * because this is where every subtle drag bug lives and the alternative is
 * asserting on rendered output.
 *
 * Returns `null` when the drop landed on nothing actionable. Callers treat that
 * as a no-op; it must never throw, because a thrown error inside `onDragEnd`
 * leaves the board with a card stuck mid-flight.
 */
export function resolveDropTarget<TCard>(args: {
  over: Over | null;
  activeId: UniqueIdentifier;
  cardsByColumn: Map<number, TCard[]>;
  getCardId: (card: TCard) => UniqueIdentifier;
}): KanbanDropTarget | null {
  const { over, activeId, cardsByColumn, getCardId } = args;
  if (!over) return null;

  const data = over.data.current as KanbanDragData | undefined;

  if (data?.type === 'zone') {
    return { kind: 'zone', zoneId: data.zoneId };
  }

  if (data?.type === 'column') {
    // Dropped on the column itself (its header, padding, or empty body) rather
    // than on a card: append. The dragged card is excluded first, so dropping a
    // card onto its own column's body does not yield an index past the end.
    const bucket = cardsByColumn.get(data.columnId) ?? [];
    const without = bucket.filter((card) => getCardId(card) !== activeId);
    return { kind: 'column', toColumnId: data.columnId, toIndex: without.length };
  }

  // Dropped on another card. Read containerId and index inside the same guard:
  // the type narrowing only holds here, and reading them outside it compiles
  // (the data bag is an index signature) but throws at runtime on any drop that
  // lands on a plain droppable.
  if (hasSortableData(over)) {
    const toColumnId = Number(over.data.current.sortable.containerId);
    if (!Number.isFinite(toColumnId)) return null;

    const bucket = cardsByColumn.get(toColumnId) ?? [];
    return {
      kind: 'column',
      toColumnId,
      toIndex: Math.min(over.data.current.sortable.index, bucket.length),
    };
  }

  return null;
}
